/**
 * Exact decimal-string helpers for finance screens.
 *
 * Every amount, quantity, price and percentage the finance contract publishes
 * is a decimal STRING with a fixed maximum scale. Nothing in this module ever
 * calls `Number()` on one: comparison and addition are done in BigInt on the
 * scaled integers, so a receipt split that must sum to the fils, or a bank
 * line split that must sum EXACTLY, is decided by arithmetic that cannot drift.
 *
 * The wire patterns (openapi/erp.yaml) reject leading zeros, a bare leading
 * dot, and a trailing dot, so `normalizeDecimal` canonicalizes user input into
 * the shape the server will actually accept rather than letting the request go
 * out and come back 422.
 */

/** NUMERIC(20,4) — money amounts (`DecimalAmountString`). */
export const AMOUNT_SCALE = 4;
/** NUMERIC(20,6) — quantities and unit prices. */
export const QUANTITY_SCALE = 6;
export const PRICE_SCALE = 6;
/** `TaxRatePercentString` — at most six fraction digits, "15" means 15%. */
export const PERCENT_SCALE = 6;

export interface DecimalOptions {
  /** Maximum fraction digits the wire pattern allows. */
  scale: number;
  /** Accept a leading "-". Defaults to false. */
  allowNegative?: boolean;
  /** Accept an exact zero. Defaults to true. */
  allowZero?: boolean;
}

const RAW_DECIMAL_RE = /^([+-]?)(\d*)(?:\.(\d*))?$/;

/**
 * Canonicalize a typed decimal into the exact wire shape, or null when the
 * text is not a decimal at all / breaches the scale / breaches the sign or
 * zero rule. `"007.50"` → `"7.50"`, `".5"` → `"0.5"`, `"12."` → `"12"`,
 * `"-0.00"` → `"0"` (negative zero is never canonical).
 */
export function normalizeDecimal(raw: string, options: DecimalOptions): string | null {
  const { scale, allowNegative = false, allowZero = true } = options;
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const match = RAW_DECIMAL_RE.exec(trimmed);
  if (match === null) return null;

  const negative = match[1] === "-";
  const integerRaw = match[2] ?? "";
  const fractionRaw = match[3] ?? "";
  if (integerRaw === "" && fractionRaw === "") return null;
  if (fractionRaw.length > scale) return null;
  if (negative && !allowNegative) return null;

  const integer = integerRaw.replace(/^0+(?=\d)/, "") || "0";
  const fraction = fractionRaw.replace(/0+$/, "");
  const isZero = !/[1-9]/.test(integer + fraction);
  if (isZero && !allowZero) return null;

  const sign = negative && !isZero ? "-" : "";
  return fraction === "" ? `${sign}${integer}` : `${sign}${integer}.${fraction}`;
}

/** True when `raw` normalizes; use for inline field validity, not for send. */
export function isDecimal(raw: string, options: DecimalOptions): boolean {
  return normalizeDecimal(raw, options) !== null;
}

/** Scale a canonical decimal string to a BigInt of `scale` fraction digits. */
function scaled(value: string, scale: number): bigint {
  const negative = value.startsWith("-");
  const unsigned = negative ? value.slice(1) : value;
  const dot = unsigned.indexOf(".");
  const integer = dot === -1 ? unsigned : unsigned.slice(0, dot);
  const fraction = dot === -1 ? "" : unsigned.slice(dot + 1);
  const padded = fraction.padEnd(scale, "0").slice(0, scale);
  const magnitude = BigInt(`${integer}${padded}`);
  return negative ? -magnitude : magnitude;
}

function unscale(value: bigint, scale: number): string {
  const negative = value < 0n;
  const digits = (negative ? -value : value).toString().padStart(scale + 1, "0");
  const integer = scale === 0 ? digits : digits.slice(0, -scale);
  const fraction = scale === 0 ? "" : digits.slice(-scale).replace(/0+$/, "");
  const sign = negative && value !== 0n ? "-" : "";
  return fraction === "" ? `${sign}${integer}` : `${sign}${integer}.${fraction}`;
}

/** Sum canonical decimal strings exactly. An empty list sums to "0". */
export function sumDecimals(values: readonly string[], scale = AMOUNT_SCALE): string {
  let total = 0n;
  for (const value of values) total += scaled(value, scale);
  return unscale(total, scale);
}

/** `a - b`, exactly. */
export function subtractDecimals(a: string, b: string, scale = AMOUNT_SCALE): string {
  return unscale(scaled(a, scale) - scaled(b, scale), scale);
}

/** -1, 0 or 1 — the sign of `a - b`, computed without a JS number. */
export function compareDecimals(a: string, b: string, scale = AMOUNT_SCALE): -1 | 0 | 1 {
  const left = scaled(a, scale);
  const right = scaled(b, scale);
  return left === right ? 0 : left < right ? -1 : 1;
}

/** True when the decimal is exactly zero, whatever its written scale. */
export function isZeroDecimal(value: string, scale = AMOUNT_SCALE): boolean {
  return scaled(value, scale) === 0n;
}

/** True when the decimal is strictly negative. */
export function isNegativeDecimal(value: string, scale = AMOUNT_SCALE): boolean {
  return scaled(value, scale) < 0n;
}

/**
 * Exact `quantity x unitPrice`, rendered at `scale`. Used only for the
 * client-side line preview — the server owns the authoritative arithmetic and
 * the tax on top of it, and this never becomes a request field.
 */
export function multiplyDecimals(
  a: string,
  b: string,
  scale = AMOUNT_SCALE,
  operandScale = QUANTITY_SCALE,
): string {
  const product = scaled(a, operandScale) * scaled(b, operandScale);
  // The product carries 2 x operandScale fraction digits; rescale by truncation
  // toward zero, which is a preview convention, never a posting rule.
  const excess = BigInt(10) ** BigInt(2 * operandScale - scale);
  return unscale(product / excess, scale);
}

/** ISO date (YYYY-MM-DD) for today, in the viewer's own calendar day. */
export function todayIsoDate(): string {
  const now = new Date();
  const month = `${now.getMonth() + 1}`.padStart(2, "0");
  const day = `${now.getDate()}`.padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}
